import React from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { QuickReference } from "./HelpSystem";
import { Dialog, DialogHeader, DialogTitle } from "../ui/Dialog";
import { FOCUS_VIEW_PATH, navSequencesFor } from "../shell/navigation";
import {
  DOCK_FOCUS_KEY,
  DOCK_LIVE_KEYS,
  DOCK_WRAP_KEYS,
  useDockState,
} from "../shell/dockStore";

/**
 * "G then <key>" jumps, built from the sidebar the role sees, so a rep is
 * never offered a page they cannot open. Without a role (a bare render) the
 * full set is listed; route guards still decide access.
 */
export const navigationShortcutsFor = (role, user) =>
  navSequencesFor(role || "admin", user).map((item) => ({
    key: `G then ${item.key.toUpperCase()}`,
    description: `Go to ${item.label}`,
    sequenceKey: item.key,
    route: item.path,
  }));

const NAVIGATION_SHORTCUTS = navigationShortcutsFor();

export const keyboardShortcuts = {
  global: [
    {
      key: "Ctrl/⌘ K",
      description: "Open the command palette",
      command: "openPalette",
    },
    {
      key: "?",
      description: "Open keyboard shortcuts",
      command: "openShortcuts",
    },
    {
      key: "Escape",
      description: "Close keyboard shortcuts",
      command: "closeShortcuts",
    },
    ...NAVIGATION_SHORTCUTS,
  ],
};

const getShortcutsForPage = (page) => [
  ...keyboardShortcuts.global,
  ...(page === "global" ? [] : keyboardShortcuts[page] || []),
];

const isEditableTarget = (target) =>
  target instanceof HTMLElement &&
  (target.isContentEditable ||
    target.tagName === "INPUT" ||
    target.tagName === "TEXTAREA" ||
    target.tagName === "SELECT");

export const getAllShortcuts = (currentPage = "global") => {
  const pageSpecific = keyboardShortcuts[currentPage] || [];

  return {
    Global: keyboardShortcuts.global,
    ...(pageSpecific.length > 0 && currentPage !== "global"
      ? {
          [`${
            currentPage.charAt(0).toUpperCase() + currentPage.slice(1)
          } Shortcuts`]: pageSpecific,
        }
      : {}),
  };
};

export const PageShortcuts = ({ page = "global" }) => (
  <QuickReference shortcuts={getShortcutsForPage(page)} />
);

export const MobileShortcuts = ({ page = "global" }) => (
  <div className="block sm:hidden">
    <QuickReference shortcuts={getShortcutsForPage(page)} />
  </div>
);

/** The dock keys that work in the dock's current mode. */
const dockShortcutsFor = (mode) => {
  if (mode === "live") {
    return [...DOCK_LIVE_KEYS, ...(FOCUS_VIEW_PATH ? [DOCK_FOCUS_KEY] : [])].map((item) => ({
      ...item,
      description: `On a call: ${item.description.charAt(0).toLowerCase()}${item.description.slice(1)}`,
    }));
  }
  if (mode === "wrap") {
    return DOCK_WRAP_KEYS.map((item) => ({
      ...item,
      description: `Wrap-up: ${item.description.charAt(0).toLowerCase()}${item.description.slice(1)}`,
    }));
  }
  return [];
};

const MOD =
  typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent || "")
    ? "⌘"
    : "Ctrl";

/**
 * The sheet's groups. Navigate: the G jumps the role's sidebar offers and
 * the rail toggle. Workspace: the palette, Ask Nexus and this sheet. Calls:
 * the dock's keys, marked live when a call or wrap-up is running. Session:
 * the dialing session's keys (components/session/SessionView.jsx).
 */
export const shortcutGroupsFor = ({ navigationShortcuts, mode = "idle" }) => {
  const dock = dockShortcutsFor(mode);
  return [
    {
      title: "Navigate",
      items: [
        ...navigationShortcuts.map((item) => ({ keys: ["G", item.sequenceKey.toUpperCase()], description: item.description, then: true })),
        { keys: ["["], description: "Collapse or expand the sidebar" },
      ],
    },
    {
      title: "Workspace",
      items: [
        { keys: [MOD, "K"], description: "Open the command palette" },
        { keys: [MOD, "J"], description: "Ask Nexus" },
        { keys: ["?"], description: "Keyboard shortcuts" },
        { keys: ["Esc"], description: "Close a menu, dialog or panel" },
      ],
    },
    {
      title: "Calls",
      live: dock.length > 0,
      items: dock.length
        ? dock.map((item) => ({ keys: item.key.split(" "), description: item.description }))
        : [...DOCK_LIVE_KEYS, ...(FOCUS_VIEW_PATH ? [DOCK_FOCUS_KEY] : [])].map((item) => ({
            keys: item.key.split(" "),
            description: item.description,
          })),
    },
    {
      title: "Session",
      items: [
        { keys: ["Space"], description: "Call the next person" },
        { keys: ["S"], description: "Skip to the next person" },
        { keys: ["I"], description: "Contact details" },
        { keys: ["N"], description: "Notes" },
        { keys: ["Q"], description: "Queue" },
        { keys: ["1–6"], description: "Pick the call outcome" },
        { keys: ["Enter"], description: "Save and go to the next call" },
        { keys: ["Esc"], description: "Leave the session" },
      ],
    },
  ];
};

const KeyChips = ({ keys, then }) => (
  <span className="sg-keys" aria-hidden="true">
    {keys.map((key, index) => (
      <React.Fragment key={`${key}-${index}`}>
        {index > 0 && then && <span className="sg-keys-then">then</span>}
        <kbd className="sg-kbd">{key}</kbd>
      </React.Fragment>
    ))}
  </span>
);

const spoken = (keys, then) => keys.join(then ? " then " : " ");

/**
 * The shortcuts sheet on ui/Dialog: two columns of grouped keys, mono key
 * chips, a scrolling body with edge fades. Focus lands on the panel, never
 * on the close button.
 */
export const ShortcutsDialog = ({ groups, open, onOpenChange }) =>
  createPortal(
  <Dialog
    open={open}
    onOpenChange={onOpenChange}
    className="max-w-[760px] sg-shortcuts"
    ariaLabel="Keyboard shortcuts"
  >
    <span tabIndex={-1} data-autofocus className="sr-only">
      Keyboard shortcuts
    </span>
    <DialogHeader onClose={() => onOpenChange(false)}>
      <DialogTitle>Keyboard shortcuts</DialogTitle>
      <p className="mt-0.5 text-[12.5px] text-sg-ink-3">Single keys work when you are not typing in a field.</p>
    </DialogHeader>
    <div className="sg-shortcuts-body">
      {groups.map((group) => (
        <section key={group.title} className="sg-shortcuts-group" aria-label={group.title}>
          <h3 className="sg-micro sg-shortcuts-title">
            {group.title}
            {group.live && <span className="sg-shortcuts-live">Now</span>}
          </h3>
          <ul>
            {group.items.map((item) => (
              <li key={`${group.title}-${item.description}`} className="sg-shortcuts-row">
                <span>{item.description}</span>
                <KeyChips keys={item.keys} then={item.then} />
                <span className="sr-only">{spoken(item.keys, item.then)}</span>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  </Dialog>,
    document.body,
  );

// The Signal shell opens this from the account menu and hides the floating
// trigger (showTrigger={false}); "?" still opens it everywhere. `role` and
// `user` shape the G-jumps to the sidebar the user sees.
export const KeyboardShortcuts = ({ showTrigger = true, role, user }) => {
  const navigate = useNavigate();
  const [showShortcuts, setShowShortcuts] = React.useState(false);
  const awaitingNavigationKey = React.useRef(false);
  const navigationTimeout = React.useRef(null);
  const { mode } = useDockState();
  const navigationShortcuts = React.useMemo(() => navigationShortcutsFor(role, user), [role, user]);
  const byKey = React.useMemo(
    () => Object.fromEntries(navigationShortcuts.map((shortcut) => [shortcut.sequenceKey, shortcut])),
    [navigationShortcuts],
  );
  const groups = shortcutGroupsFor({ navigationShortcuts, mode });

  React.useEffect(() => {
    const clearNavigationSequence = () => {
      awaitingNavigationKey.current = false;

      if (navigationTimeout.current) {
        window.clearTimeout(navigationTimeout.current);
        navigationTimeout.current = null;
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape" && showShortcuts) {
        event.preventDefault();
        setShowShortcuts(false);
        clearNavigationSequence();
        return;
      }

      if (
        isEditableTarget(event.target) ||
        event.ctrlKey ||
        event.metaKey ||
        event.altKey
      ) {
        clearNavigationSequence();
        return;
      }

      if (event.key === "?") {
        event.preventDefault();
        setShowShortcuts(true);
        clearNavigationSequence();
        return;
      }

      const key = event.key.toLowerCase();

      if (awaitingNavigationKey.current) {
        const shortcut = byKey[key];
        clearNavigationSequence();
        // The key after G belongs to the jump, never to a single-key
        // shortcut (the call dock's H, T or M): this listener runs in the
        // capture phase and marks the key handled.
        if (key.length === 1) event.preventDefault();

        if (shortcut) {
          setShowShortcuts(false);
          navigate(shortcut.route);
        }
        return;
      }

      if (key === "g") {
        awaitingNavigationKey.current = true;
        navigationTimeout.current = window.setTimeout(
          clearNavigationSequence,
          1500,
        );
      }
    };

    window.addEventListener("keydown", handleKeyDown, true);
    return () => {
      window.removeEventListener("keydown", handleKeyDown, true);
      clearNavigationSequence();
    };
  }, [byKey, navigate, showShortcuts]);

  return (
    <>
      {showTrigger && (
      <div className="fixed bottom-4 left-4 z-40">
        <button
          type="button"
          onClick={() => setShowShortcuts(true)}
          className="sg-btn sg-btn-secondary sg-btn-icon"
          aria-label="Show keyboard shortcuts"
        >
          <span className="font-mono text-[13px]" aria-hidden="true">
            ?
          </span>
        </button>
      </div>
      )}

      <ShortcutsDialog groups={groups} open={showShortcuts} onOpenChange={setShowShortcuts} />
    </>
  );
};

export default PageShortcuts;
